import React, { useState, useEffect } from 'react';
import { X, User, Users as UsersIcon, Baby, Plane, CheckCircle2, AlertCircle, Loader2, CreditCard, Apple } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n/LanguageContext';
import { api } from '../utils/apiClient';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

// Initialize Stripe
let stripePromise = null;

const initializeStripe = async () => {
  if (!stripePromise) {
    try {
      const response = await api.get('/api/payments/config');
      stripePromise = loadStripe(response.publishable_key);
    } catch (error) {
      console.error('Error loading Stripe:', error);
      // Fallback to test key if backend doesn't provide one
      stripePromise = loadStripe('pk_test_51QsdxfP8JqCHHXoE9tzvOj7YqFvDe3OVgXlm6WEG2BzHOp4bvUY9rPm4H8rA7VZ0NkoQNpBAP5eOCYpJW61lJAIJ00vfwPYdOk');
    }
  }
  return stripePromise;
};

// Payment Form Component
const PaymentForm = ({ totalPrice, onPaymentSuccess, onError, bookingData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useLanguage();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: 'MX',
        currency: 'mxn',
        total: {
          label: 'Total a Pagar',
          amount: Math.round(totalPrice * 100), // Stripe usa centavos
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check if Apple Pay/Google Pay is available
      pr.canMakePayment().then(result => {
        if (result) {
          setPaymentRequest(pr);
        }
      });

      pr.on('paymentmethod', async (ev) => {
        setIsProcessing(true);
        try {
          // Process payment with the payment method
          await processPayment(ev.paymentMethod.id);
          ev.complete('success');
          onPaymentSuccess();
        } catch (error) {
          ev.complete('fail');
          onError(error.message);
        } finally {
          setIsProcessing(false);
        }
      });
    }
  }, [stripe, totalPrice]);

  const processPayment = async (paymentMethodId) => {
    try {
      // Create payment intent
      const intentResponse = await api.post('/api/payments/create-payment-intent', {
        amount: Math.round(totalPrice * 100),
        currency: 'mxn',
        booking_id: bookingData.booking_id || 'temp_' + Date.now()
      });

      // Confirm payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        intentResponse.client_secret,
        paymentMethodId ? { payment_method: paymentMethodId } : {
          payment_method: {
            card: elements.getElement(CardElement),
          }
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      return paymentIntent;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      await processPayment();
      onPaymentSuccess();
    } catch (error) {
      onError(error.message || 'Error procesando el pago');
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Apple Pay / Google Pay Button */}
      {paymentRequest && (
        <div className="mb-4">
          <Label className="mb-2 block">{t('search.searching') === 'Searching...' ? 'Quick Payment' : 'Pago Rápido'}</Label>
          <PaymentRequestButtonElement
            options={{ paymentRequest }}
            className="PaymentRequestButton"
          />
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('search.searching') === 'Searching...' ? 'Or pay with card' : 'O paga con tarjeta'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Card Element */}
      <div>
        <Label className="mb-2 block">
          <CreditCard className="w-4 h-4 inline mr-2" />
          {t('search.searching') === 'Searching...' ? 'Card Information' : 'Información de Tarjeta'}
        </Label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 flex items-center">
          <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0" />
          {t('search.searching') === 'Searching...'
            ? 'Your payment is secure. We use Stripe to process payments safely.'
            : 'Tu pago es seguro. Usamos Stripe para procesar pagos de forma segura.'}
        </p>
      </div>

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            {t('search.searching') === 'Searching...' ? 'Processing...' : 'Procesando...'}
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            {t('search.searching') === 'Searching...' ? `Pay $${totalPrice.toLocaleString()} MXN` : `Pagar $${totalPrice.toLocaleString()} MXN`}
          </>
        )}
      </Button>
    </form>
  );
};

// PayPal Payment Component
const PayPalPayment = ({ totalPrice, onPaymentSuccess, onError, bookingData }) => {
  const { t } = useLanguage();
  const [paypalClientId, setPaypalClientId] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const response = await api.get('/api/payments/paypal/config');
        setPaypalClientId(response.client_id);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading PayPal config:', error);
        onError('Error al cargar configuración de PayPal');
        setIsLoading(false);
      }
    };
    fetchPayPalConfig();
  }, [onError]);

  const createOrder = async () => {
    try {
      const response = await api.post('/api/payments/paypal/create-order', {
        amount: totalPrice,
        currency: 'MXN',
        booking_id: bookingData.booking_id || 'temp-' + Date.now(),
        description: `Aerotucán - Vuelo ${bookingData.flight_number || ''}`
      });
      return response.order_id;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await api.post('/api/payments/paypal/capture-order', {
        order_id: data.orderID
      });
      
      if (response.status === 'COMPLETED') {
        onPaymentSuccess(response.capture_id);
      } else {
        onError('El pago no se completó correctamente');
      }
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      onError('Error al procesar el pago de PayPal');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (!paypalClientId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-800">
          PayPal no está configurado. Por favor contacta al administrador.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-800 flex items-center">
          <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0" />
          {t('search.searching') === 'Searching...'
            ? 'Pay securely with PayPal. Your payment information is protected.'
            : 'Paga de forma segura con PayPal. Tu información de pago está protegida.'}
        </p>
      </div>

      <PayPalScriptProvider 
        options={{ 
          "client-id": paypalClientId,
          currency: "MXN"
        }}
      >
        <PayPalButtons
          style={{
            layout: 'vertical',
            color: 'blue',
            shape: 'rect',
            label: 'pay'
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(err) => {
            console.error('PayPal error:', err);
            onError('Error al procesar el pago con PayPal');
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

const BookingModalNew = ({ 
  isOpen, 
  onClose, 
  flight, 
  tripType = 'one-way', 
  outboundFlight = null, 
  returnFlight = null,
  adults = 1,
  children = 0,
  babies = 0
}) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [pricingConfig, setPricingConfig] = useState({
    adult_percentage: 100.0,
    child_percentage: 75.0,
    baby_percentage: 10.0
  });
  
  const totalPassengers = adults + children + babies;
  
  // Inicializar array de pasajeros
  const initializePassengers = () => {
    const passengers = [];
    for (let i = 0; i < adults; i++) {
      passengers.push({ type: 'adult', index: i });
    }
    for (let i = 0; i < children; i++) {
      passengers.push({ type: 'child', index: i });
    }
    for (let i = 0; i < babies; i++) {
      passengers.push({ type: 'baby', index: i });
    }
    return passengers.map((p, idx) => ({
      id: idx,
      passengerType: p.type,
      firstName: '',
      lastName: '',
      email: idx === 0 ? '' : '',
      phone: idx === 0 ? '' : '',
      nationality: '',
      idType: 'passport',
      idNumber: '',
      dateOfBirth: ''
    }));
  };

  const [passengersData, setPassengersData] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const [bookingDataForPayment, setBookingDataForPayment] = useState(null);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // CRÍTICO: Reinicializar pasajeros cuando cambien adults/children/babies o cuando se abra el modal
  useEffect(() => {
    if (isOpen) {
      console.log(`🔄 Modal abierto - Inicializando ${adults} adultos, ${children} niños, ${babies} bebés`);
      setPassengersData(initializePassengers());
      setStep(1);
      setPaymentMethod('card');
      setError('');
      setBookingConfirmed(false);
      setBookingNumber('');
      setTermsAccepted(false);
      
      // Fetch pricing configuration from backend
      const fetchPricingConfig = async () => {
        try {
          const config = await api.get('/api/admin/pricing/config');
          console.log('✅ Pricing config loaded:', config);
          setPricingConfig(config);
        } catch (error) {
          console.error('Error loading pricing config, using defaults:', error);
          // Use default values if fetch fails
        }
      };
      fetchPricingConfig();
    }
  }, [isOpen, adults, children, babies]);

  // Initialize Stripe when modal opens
  useEffect(() => {
    if (isOpen && !stripeLoaded) {
      initializeStripe().then(() => setStripeLoaded(true));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Cálculo de precio dinámico usando configuración del backend
  const basePrice = flight?.price || 0;
  const adultsPrice = adults * basePrice * (pricingConfig.adult_percentage / 100);
  const childrenPrice = children * basePrice * (pricingConfig.child_percentage / 100);
  const babiesPrice = babies * basePrice * (pricingConfig.baby_percentage / 100);
  let totalPrice = adultsPrice + childrenPrice + babiesPrice;
  
  // Si es viaje redondo, duplicar el precio
  if (tripType === 'round-trip' && returnFlight) {
    totalPrice = totalPrice * 2;
  }

  const handlePassengerChange = (passengerId, field, value) => {
    setPassengersData(prev => prev.map(p => 
      p.id === passengerId ? { ...p, [field]: value } : p
    ));
  };

  const validatePassengerData = () => {
    if (passengersData.length === 0) {
      setError(t('search.searching') === 'Searching...' 
        ? 'No passenger data found. Please try again.'
        : 'No se encontraron datos de pasajeros. Por favor intenta de nuevo.');
      return false;
    }

    const contactPerson = passengersData[0];
    
    if (!contactPerson.firstName || !contactPerson.lastName || !contactPerson.email || !contactPerson.phone) {
      setError(t('search.searching') === 'Searching...' 
        ? 'Please fill in all contact information for the first passenger'
        : 'Por favor completa toda la información de contacto del primer pasajero');
      return false;
    }
    
    // Validar todos los pasajeros
    for (let i = 0; i < passengersData.length; i++) {
      const passenger = passengersData[i];
      if (!passenger.firstName || !passenger.lastName || !passenger.nationality || !passenger.idType || !passenger.idNumber) {
        setError(t('search.searching') === 'Searching...'
          ? `Please complete all required fields for passenger ${i + 1}`
          : `Por favor completa todos los campos requeridos del pasajero ${i + 1}`);
        return false;
      }
    }
    
    setError('');
    return true;
  };

  const handleContinueToPayment = () => {
    if (validatePassengerData()) {
      setStep(2);
    }
  };

  const handleConfirmBooking = async () => {
    // Si es pago con tarjeta, el componente PaymentForm manejará el pago
    if (paymentMethod === 'card') {
      return; // El formulario de pago se encargará
    }

    // Para otros métodos de pago (transferencia, etc.)
    await createBooking();
  };

  const createBooking = async () => {
    setIsLoading(true);
    setError('');

    try {
      const contactPerson = passengersData[0];
      
      const bookingData = {
        flight_id: flight?.id || flight?.flightNumber,
        return_flight_id: returnFlight?.id || null,
        origin: flight?.origin,
        destination: flight?.destination,
        departure_date: flight?.date,
        departure_time: flight?.departureTime,
        return_date: returnFlight?.date || null,
        return_time: returnFlight?.departureTime || null,
        trip_type: tripType,
        contact_person: {
          passenger_type: contactPerson.passengerType,
          first_name: contactPerson.firstName,
          last_name: contactPerson.lastName,
          email: contactPerson.email,
          phone: contactPerson.phone,
          nationality: contactPerson.nationality,
          id_type: contactPerson.idType,
          id_number: contactPerson.idNumber,
          date_of_birth: contactPerson.dateOfBirth || null
        },
        passengers: passengersData.map(p => ({
          passenger_type: p.passengerType,
          first_name: p.firstName,
          last_name: p.lastName,
          email: (p.email && p.email.trim() !== '') ? p.email : contactPerson.email,
          phone: (p.phone && p.phone.trim() !== '') ? p.phone : contactPerson.phone,
          nationality: p.nationality,
          id_type: p.idType,
          id_number: p.idNumber,
          date_of_birth: p.dateOfBirth || null
        })),
        num_passengers: totalPassengers,
        is_private_flight: false,
        price_per_person: basePrice,
        total_price: totalPrice,
        currency: 'MXN',
        special_requests: ''
      };

      const response = await api.post('/api/bookings/', bookingData);
      
      setBookingNumber(response.booking_id);
      setBookingDataForPayment({ ...bookingData, booking_id: response.booking_id });
      
      // Si NO es pago con tarjeta, mostrar confirmación inmediatamente
      if (paymentMethod !== 'card') {
        setBookingConfirmed(true);
        setStep(3);
      }
      
      setIsLoading(false);
      return response;
    } catch (error) {
      console.error('Error creating booking:', error);
      setError(t('search.searching') === 'Searching...'
        ? 'An error occurred while processing your booking. Please try again.'
        : 'Ocurrió un error al procesar tu reserva. Por favor intenta de nuevo.');
      setIsLoading(false);
      throw error;
    }
  };

  const handlePaymentSuccess = async () => {
    setBookingConfirmed(true);
    setStep(3);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    setStep(1);
    setPassengersData([]);
    setPaymentMethod('card');
    setBookingConfirmed(false);
    setBookingNumber('');
    setError('');
    onClose();
  };

  const getPassengerTypeLabel = (type, index) => {
    const isEnglish = t('search.searching') === 'Searching...';
    if (type === 'adult') return isEnglish ? `Adult ${index + 1}` : `Adulto ${index + 1}`;
    if (type === 'child') return isEnglish ? `Child ${index + 1}` : `Niño ${index + 1}`;
    return isEnglish ? `Baby ${index + 1}` : `Bebé ${index + 1}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-5xl my-8 bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Plane className="w-6 h-6" />
            <h2 className="text-2xl font-bold">
              {t('search.searching') === 'Searching...' ? 'Book Your Flight' : 'Reserva tu Vuelo'}
            </h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[calc(90vh-100px)] overflow-y-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8 space-x-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-blue-900' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {/* Debug Info */}
          <div className="mb-4 p-3 bg-gray-100 rounded text-xs text-gray-600">
            Debug: {adults} adultos, {children} niños, {babies} bebés = {passengersData.length} formularios
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Step 1: Passenger Information */}
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('search.searching') === 'Searching...' ? 'Passenger Information' : 'Información de Pasajeros'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {t('search.searching') === 'Searching...' 
                    ? 'Please provide information for all passengers. The first passenger will be the main contact.'
                    : 'Por favor proporciona la información de todos los pasajeros. El primer pasajero será el contacto principal.'}
                </p>
              </div>

              {passengersData.map((passenger, idx) => {
                const typeIndex = passengersData.slice(0, idx).filter(p => p.passengerType === passenger.passengerType).length;
                
                return (
                  <Card key={passenger.id} className="bg-gray-50">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        {passenger.passengerType === 'adult' && <UsersIcon className="w-6 h-6 text-blue-900" />}
                        {passenger.passengerType === 'child' && <User className="w-6 h-6 text-amber-600" />}
                        {passenger.passengerType === 'baby' && <Baby className="w-6 h-6 text-green-600" />}
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">
                            {getPassengerTypeLabel(passenger.passengerType, typeIndex)}
                          </h4>
                          {idx === 0 && (
                            <p className="text-sm text-gray-600">
                              {t('search.searching') === 'Searching...' ? 'Main Contact' : 'Contacto Principal'}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'First Name' : 'Nombre'} *</Label>
                          <Input
                            value={passenger.firstName}
                            onChange={(e) => handlePassengerChange(passenger.id, 'firstName', e.target.value)}
                            placeholder={t('search.searching') === 'Searching...' ? 'John' : 'Juan'}
                          />
                        </div>
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'Last Name' : 'Apellidos'} *</Label>
                          <Input
                            value={passenger.lastName}
                            onChange={(e) => handlePassengerChange(passenger.id, 'lastName', e.target.value)}
                            placeholder={t('search.searching') === 'Searching...' ? 'Doe' : 'Pérez García'}
                          />
                        </div>
                        {idx === 0 && (
                          <>
                            <div>
                              <Label>Email *</Label>
                              <Input
                                type="email"
                                value={passenger.email}
                                onChange={(e) => handlePassengerChange(passenger.id, 'email', e.target.value)}
                                placeholder="email@ejemplo.com"
                              />
                            </div>
                            <div>
                              <Label>{t('search.searching') === 'Searching...' ? 'Phone' : 'Teléfono'} *</Label>
                              <Input
                                type="tel"
                                value={passenger.phone}
                                onChange={(e) => handlePassengerChange(passenger.id, 'phone', e.target.value)}
                                placeholder="+52 951 123 4567"
                              />
                            </div>
                          </>
                        )}
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'Nationality' : 'Nacionalidad'} *</Label>
                          <select
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            value={passenger.nationality}
                            onChange={(e) => handlePassengerChange(passenger.id, 'nationality', e.target.value)}
                          >
                            <option value="">{t('search.searching') === 'Searching...' ? 'Select' : 'Seleccionar'}</option>
                            <option value="MX">México</option>
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="ES">España</option>
                            <option value="AR">Argentina</option>
                            <option value="CO">Colombia</option>
                            <option value="PE">Perú</option>
                            <option value="CL">Chile</option>
                            <option value="OTHER">{t('search.searching') === 'Searching...' ? 'Other' : 'Otro'}</option>
                          </select>
                        </div>
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'ID Type' : 'Tipo de Identificación'} *</Label>
                          <select
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            value={passenger.idType}
                            onChange={(e) => handlePassengerChange(passenger.id, 'idType', e.target.value)}
                          >
                            <option value="passport">{t('search.searching') === 'Searching...' ? 'Passport' : 'Pasaporte'}</option>
                            <option value="national_id">{t('search.searching') === 'Searching...' ? 'National ID' : 'INE/IFE'}</option>
                            <option value="driver_license">{t('search.searching') === 'Searching...' ? 'Driver License' : 'Licencia de Conducir'}</option>
                            <option value="other">{t('search.searching') === 'Searching...' ? 'Other' : 'Otro'}</option>
                          </select>
                        </div>
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'ID Number' : 'Número de Identificación'} *</Label>
                          <Input
                            value={passenger.idNumber}
                            onChange={(e) => handlePassengerChange(passenger.id, 'idNumber', e.target.value)}
                            placeholder="ABC123456"
                          />
                        </div>
                        <div>
                          <Label>{t('search.searching') === 'Searching...' ? 'Date of Birth' : 'Fecha de Nacimiento'}</Label>
                          <Input
                            type="date"
                            value={passenger.dateOfBirth}
                            onChange={(e) => handlePassengerChange(passenger.id, 'dateOfBirth', e.target.value)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Flight Details Summary */}
              <Card className="bg-blue-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    {t('search.searching') === 'Searching...' ? 'Flight Details' : 'Detalles del Vuelo'}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('search.searching') === 'Searching...' ? 'Route' : 'Ruta'}:</span>
                      <span className="font-semibold">{flight?.origin} → {flight?.destination}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('search.searching') === 'Searching...' ? 'Date' : 'Fecha'}:</span>
                      <span className="font-semibold">{flight?.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('search.searching') === 'Searching...' ? 'Time' : 'Hora'}:</span>
                      <span className="font-semibold">{flight?.departureTime}</span>
                    </div>
                    {tripType === 'round-trip' && returnFlight && (
                      <>
                        <div className="border-t border-blue-200 pt-3 mt-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('search.searching') === 'Searching...' ? 'Return' : 'Regreso'}:</span>
                            <span className="font-semibold">{returnFlight?.origin} → {returnFlight?.destination}</span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-gray-600">{t('search.searching') === 'Searching...' ? 'Date' : 'Fecha'}:</span>
                            <span className="font-semibold">{returnFlight?.date}</span>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="border-t border-blue-200 pt-3 mt-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('search.adults')}:</span>
                        <span className="font-semibold">{adults} × ${basePrice.toLocaleString()} = ${adultsPrice.toLocaleString()}</span>
                      </div>
                      {children > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('search.children')} ({pricingConfig.child_percentage}%):</span>
                          <span className="font-semibold">{children} × ${(basePrice * pricingConfig.child_percentage / 100).toLocaleString()} = ${childrenPrice.toLocaleString()}</span>
                        </div>
                      )}
                      {babies > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('search.babies')} ({pricingConfig.baby_percentage}%):</span>
                          <span className="font-semibold">{babies} × ${(basePrice * pricingConfig.baby_percentage / 100).toLocaleString()} = ${babiesPrice.toLocaleString()}</span>
                        </div>
                      )}
                      {tripType === 'round-trip' && (
                        <div className="flex justify-between text-amber-600 mt-2">
                          <span>{t('search.searching') === 'Searching...' ? 'Round trip (×2)' : 'Ida y vuelta (×2)'}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between text-lg font-bold text-blue-900 pt-3 border-t border-blue-200">
                      <span>Total:</span>
                      <span>${totalPrice.toLocaleString()} MXN</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-blue-200 space-y-2">
                    <p className="text-sm font-semibold text-gray-900 mb-3">
                      {t('search.searching') === 'Searching...' ? 'Baggage Policy' : 'Política de Equipaje'}:
                    </p>
                    <div className="space-y-2 text-xs text-gray-700">
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <div>
                          <p className="font-semibold">
                            {t('search.searching') === 'Searching...' 
                              ? '1 checked baggage up to 15 kg (included)'
                              : '1 maleta documentada de hasta 15 kg (incluida)'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <div>
                          <p className="font-semibold">
                            {t('search.searching') === 'Searching...' 
                              ? '1 carry-on baggage of 5 kg (included)'
                              : '1 maleta de mano de 5 kg (incluida)'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start bg-amber-50 -mx-2 px-2 py-2 rounded">
                        <span className="text-amber-600 mr-2">💰</span>
                        <div>
                          <p className="text-amber-800 font-semibold">
                            {t('search.searching') === 'Searching...' 
                              ? 'Extra weight: $34 MXN per additional kg'
                              : 'Peso extra: $34 MXN por kg adicional'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Términos y Condiciones - Exacto como la imagen */}
              <div className="border-2 border-yellow-400 bg-yellow-50 rounded-lg p-6">
                <p className="text-sm text-gray-700 mb-4">
                  📋 Te recomendamos consultar los{' '}
                  <a
                    href="https://www.aerotucan.com/php/terminos.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    términos y condiciones del servicio
                  </a>
                  ,{' '}
                  <a
                    href="https://www.aerotucan.com/php/condiciones.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    aviso de privacidad
                  </a>
                  , así como el{' '}
                  <a
                    href="https://www.aerotucan.com/php/condiciones.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Contrato del Servicio del Transporte Aéreo
                  </a>
                  .
                </p>
                
                <label className="flex items-start space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-800">
                    He leído y estoy de acuerdo con los términos y condiciones, aviso de privacidad, así como el contrato del servicio de transporte aéreo.
                  </span>
                </label>
                
                {!termsAccepted && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <span className="text-red-600">⚠️</span>
                    <span className="text-sm text-red-700">
                      Debes aceptar los términos y condiciones para continuar
                    </span>
                  </div>
                )}
              </div>

              <Button 
                onClick={handleContinueToPayment}
                disabled={!termsAccepted}
                className={`w-full py-6 text-lg ${
                  !termsAccepted 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-blue-900 hover:bg-blue-800 text-white'
                }`}
              >
                {t('search.searching') === 'Searching...' ? 'Continue to Payment' : 'Continuar al Pago'}
              </Button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900">
                {t('search.searching') === 'Searching...' ? 'Payment Method' : 'Método de Pago'}
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all flex items-center ${
                    paymentMethod === 'card' ? 'border-blue-900 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  <div>
                    <span className="font-semibold">
                      {t('search.searching') === 'Searching...' ? 'Credit/Debit Card' : 'Tarjeta de Crédito/Débito'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('search.searching') === 'Searching...' ? 'Pay securely with Stripe. Apple Pay available.' : 'Paga de forma segura con Stripe. Apple Pay disponible.'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all flex items-center ${
                    paymentMethod === 'paypal' ? 'border-blue-900 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516c-.075.476.275.909.753.909h4.606c.523 0 .967-.382 1.05-.9l1.045-6.628h2.19c4.292 0 7.659-1.747 8.647-6.797.03-.149.054-.294.077-.437.201-1.284.107-2.293-.423-3.183z"/>
                  </svg>
                  <div>
                    <span className="font-semibold">
                      {t('search.searching') === 'Searching...' ? 'PayPal' : 'PayPal'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('search.searching') === 'Searching...' ? 'Pay quickly and securely with your PayPal account' : 'Paga rápido y seguro con tu cuenta PayPal'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentMethod('transfer')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    paymentMethod === 'transfer' ? 'border-blue-900 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-semibold">
                    {t('search.searching') === 'Searching...' ? 'Bank Transfer' : 'Transferencia Bancaria'}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('search.searching') === 'Searching...' ? 'You will receive bank details via email' : 'Recibirás los datos bancarios por correo'}
                  </p>
                </button>
              </div>

              {/* Stripe Payment Form */}
              {paymentMethod === 'card' && stripeLoaded && (
                <div className="mt-6">
                  <Elements stripe={stripePromise}>
                    <PaymentForm
                      totalPrice={totalPrice}
                      onPaymentSuccess={async () => {
                        await createBooking();
                        handlePaymentSuccess();
                      }}
                      onError={handlePaymentError}
                      bookingData={bookingDataForPayment || {}}
                    />
                  </Elements>
                </div>
              )}

              {/* PayPal Payment */}
              {paymentMethod === 'paypal' && (
                <div className="mt-6">
                  <PayPalPayment
                    totalPrice={totalPrice}
                    onPaymentSuccess={async () => {
                      await createBooking();
                      handlePaymentSuccess();
                    }}
                    onError={handlePaymentError}
                    bookingData={bookingDataForPayment || {}}
                  />
                </div>
              )}

              {/* Other payment methods */}
              {paymentMethod !== 'card' && paymentMethod !== 'paypal' && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      {t('search.searching') === 'Searching...'
                        ? 'After confirming your booking, you will receive payment instructions via email and WhatsApp.'
                        : 'Después de confirmar tu reserva, recibirás las instrucciones de pago por correo y WhatsApp.'}
                    </p>
                  </div>

                  <div className="flex space-x-4">
                    <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                      {t('search.searching') === 'Searching...' ? 'Back' : 'Atrás'}
                    </Button>
                    <Button 
                      onClick={handleConfirmBooking} 
                      className="flex-1 bg-blue-900 hover:bg-blue-800"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('search.searching') === 'Searching...' ? 'Processing...' : 'Procesando...'}
                        </>
                      ) : (
                        t('search.searching') === 'Searching...' ? 'Confirm Booking' : 'Confirmar Reserva'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {paymentMethod === 'card' && (
                <Button onClick={() => setStep(1)} variant="outline" className="w-full">
                  {t('search.searching') === 'Searching...' ? 'Back' : 'Atrás'}
                </Button>
              )}
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && bookingConfirmed && (
            <div className="text-center space-y-6">
              <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto" />
              <h3 className="text-3xl font-bold text-gray-900">
                {t('search.searching') === 'Searching...' ? 'Booking Confirmed!' : '¡Reserva Confirmada!'}
              </h3>
              <p className="text-lg text-gray-600">
                {t('search.searching') === 'Searching...' 
                  ? 'Thank you for your booking. We look forward to flying with you!'
                  : '¡Gracias por tu reserva! Esperamos volar contigo pronto.'}
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  {t('search.searching') === 'Searching...' ? 'Booking Number' : 'Número de Reserva'}:
                </p>
                <p className="text-3xl font-bold text-blue-900">{bookingNumber}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {t('search.searching') === 'Searching...'
                    ? 'We have sent the confirmation details to your email and WhatsApp.'
                    : 'Hemos enviado los detalles de confirmación a tu correo y WhatsApp.'}
                </p>
              </div>
              <Button onClick={handleClose} className="bg-blue-900 hover:bg-blue-800">
                {t('search.searching') === 'Searching...' ? 'Close' : 'Cerrar'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModalNew;
